import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router'

import { GroupHeader } from '../../components/GroupHeader'
import {
  BeforeCell, ColumnHead, EveningCell, FeltCell, JournalDateCell, JournalPills, SleepCell, TagsCell,
} from '../../components/history/JournalCells'
import {
  journalPeriods, summaryFacts, unavailableText, usualCoverageText, type JournalPeriod,
} from '../../components/history/journalFacts'
import { PageEnd } from '../../components/PageEnd'
import { PageHeader } from '../../components/PageHeader'
import { HistoryDay } from '../../components/history/HistoryDay'
import { TrendChart } from '../../components/trend/TrendChart'
import {
  Card, DataTable, Icon, LoadMore, Pill, ProblemNotice, RecordingStatusPill, RESEARCH_NOTES_ID,
  ResearchNotes, SegmentedControl, type Column, type RowGroup,
} from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import {
  getDailyStatus, getJournalSummary, listRecordings,
  type JournalGrouping, type JournalSummaryResponse, type RecordingRecord,
} from '../../lib/api'
import { useResource } from '../../lib/useResource'
import { useRetryLanding } from '../../lib/useRetryLanding'
import { hasDailyStatus, readDailyStatus, type DailyStatusResult } from '../../lib/dailyStatus'
import { EXPERIMENTS_ENABLED, PAIRED_SESSION_ENABLED } from '../../lib/features'
import { formatConfidence, formatDateTime, formatSessionDate } from '../../lib/format'
import {
  HISTORY_VIEW_OPTIONS, JOURNAL_GROUPING_OPTIONS, readStoredHistoryView,
  readStoredJournalGrouping, storeHistoryView, storeJournalGrouping,
  type HistoryView, type JournalGroupingChoice,
} from '../../lib/historyView'
import { pairLinkOf, type PairLink } from '../../lib/pair'
import { historyRange, historyRangeText } from '../../lib/historyRange'
import { ApiError } from '../../lib/problem'
import { CONDITION_WORDS, RECORDING_OUTCOME } from '../../lib/recordings'
import { provenanceWords } from '../../lib/selfReport'
import type { FanOutEntry } from '../../lib/useFanOut'
import { useFanOut } from '../../lib/useFanOut'
import type { Page } from '../../lib/usePagedList'
import { usePagedList } from '../../lib/usePagedList'
import { ERROR_CODE_COPY, presentWarnings } from '../../lib/warnings'
import '../../styles/experiments-day.css'

/**
 * History — every recording, newest upload first, with what is known about each.
 *
 * ── THE LIST IS ORDERED BY ONE DATE AND SHOWS ANOTHER ───────────────────────
 *
 * `GET /users/{id}/recordings` pages newest-first by **`created_at`**
 * (`api.ts:369-376`); the column a person reads is **`session_date`**. Those
 * disagree for any backfilled or derived window, and the fixtures are built that
 * way deliberately — page one is 11 September then 30 August. The order is the
 * server's and is left alone; every date shown is the recording's own, and the
 * upload time is available on the row rather than inferred from position.
 *
 * ── WHAT THIS SCREEN COSTS ──────────────────────────────────────────────────
 *
 * `RecordingRecord` carries no score and no confidence, so every confidence
 * value on this page is a separate `GET /recordings/{id}/daily-status`. §4.1
 * bounds that: no request for a row that cannot have a result, visible per-row
 * loading and error states, and nothing unlimited. `useFanOut` is that rule;
 * this file supplies the eligibility predicate and renders the five states.
 */

export function History() {
  const { account } = useAuth()

  if (EXPERIMENTS_ENABLED && account?.experiments_enabled_for_me === true) return <ExperimentHistory key={account?.user_id ?? 'none'} userId={account?.user_id ?? null} />

  /**
   * A REMOUNT ON AN ACCOUNT CHANGE, rather than a reset.
   *
   * §4.5's last clause is "clear user-specific cached data on sign-out/account
   * changes". Sign-out is free — the console unmounts and every hook's state
   * goes with it. An account change without a sign-out is not: `refreshAccount`
   * can swap `account` in place, and `useFanOut` holds `seen` in a **ref**,
   * which no render-time reset can reach. Recording ids are device-minted
   * timestamps (`recording_id` IS `session_id`), so two accounts' ids are not
   * disjoint by construction; the backend scopes by `(user_id, recording_id)`
   * and that ref does not. A `key` is one line and clears everything inside.
   */
  return (
    <HistoryBody key={account?.user_id ?? 'none'} userId={account?.user_id ?? null} />
  )
}

/** The Day read replaces the legacy list while selected; no hidden trend fan-out. */
function ExperimentHistory({ userId }: { userId: string | null }) {
  const [view, setView] = useState<HistoryView>(readStoredHistoryView)
  const chooseView = useCallback((next: HistoryView) => {
    setView(next)
    storeHistoryView(next)
  }, [])
  return view === 'day'
    ? <HistoryDay userId={userId} onView={chooseView} />
    : <HistoryBody userId={userId} experimentView={view} onExperimentView={chooseView} />
}

/** The row's pair link, or null — and always null with the flag off, so the table is byte-for-byte today's. */
const pairOf = (r: RecordingRecord): PairLink | null => (PAIRED_SESSION_ENABLED ? pairLinkOf(r) : null)

/**
 * The Journal view's seven columns, in the owner's order (ruling §5.3).
 *
 * A MODULE CONSTANT, unlike the Readings columns built inside the component:
 * not one of these cells reads anything outside the row it is handed — no
 * Daily Status, no fan-out entry, no score — so there is nothing to close over.
 * The Journal view therefore costs the page no request it was not already
 * making.
 *
 * Every column carries a `label` as well as a `header`, and that is not
 * decoration: `DataTable` uses the label as the `<dt>` of its card rendering
 * below 768px (concept §C — one card per recording, the fields in this same
 * order), and falls back to the `header`, which here is an element and cannot
 * be read as a string.
 */
const JOURNAL_COLUMNS: Column<RecordingRecord>[] = [
  {
    key: 'date',
    header: 'Date',
    label: 'Recording date',
    render: (r) => <JournalDateCell record={r} />,
  },
  {
    key: 'felt',
    header: <ColumnHead title="How you felt" anchor="right now" />,
    label: 'How you felt, right now',
    render: (r) => <FeltCell report={r.self_report} />,
  },
  {
    key: 'sleep',
    header: <ColumnHead title="Sleep" anchor="last night" />,
    label: 'Sleep, last night',
    render: (r) => <SleepCell report={r.self_report} />,
  },
  {
    key: 'before',
    header: <ColumnHead title="Before the recording" anchor="since waking" />,
    label: 'Before the recording, since waking',
    render: (r) => <BeforeCell report={r.self_report} />,
  },
  {
    key: 'evening',
    header: <ColumnHead title="The evening before" anchor="last 24 h" />,
    label: 'The evening before, last 24 h',
    render: (r) => <EveningCell report={r.self_report} />,
  },
  {
    key: 'tags',
    header: 'Tags',
    label: 'Tags',
    render: (r) => <TagsCell report={r.self_report} />,
  },
  {
    key: 'provenance',
    // The column labels the pills under it and has no word of its own to show
    // -- and an empty `<th>` is a header a screen reader cannot announce.
    header: <span className="visually-hidden">Journal</span>,
    label: 'Journal',
    render: (r) => <JournalPills record={r} />,
  },
]

/**
 * The footer sentences, from the concept's own two panels.
 *
 * Module constants rather than inline JSX, so the one place the words live is
 * the one place a reviewer compares them with
 * `design-assets/journal-concept/brainspot-journal-concept.html`. They are
 * presentation copy and not science: no claim, no adjective and no comparison
 * (`src/test/clientScience.test.ts`).
 */
const JOURNAL_FOOT_DAYS =
  'Your answers, exactly as you wrote them. BrainSpot does not yet compare them with your '
  + 'readings; when it can, it will say what it compared and on which days.'
const JOURNAL_FOOT_GROUPED =
  'Averages and counts of your own answers, computed by BrainSpot. “Your usual” is every '
  + 'journal you have filed. Nothing here is compared with your readings.'

function HistoryBody({ userId, experimentView, onExperimentView }: {
  userId: string | null
  experimentView?: HistoryView
  onExperimentView?: (view: HistoryView) => void
}) {
  // Total rather than conditional, as Today.tsx:64-70: hooks cannot be called
  // behind an `if`, and this renders only inside RequireAuth, so the null branch
  // is a type obligation rather than a state a user reaches.
  const fetchPage = useCallback(
    (cursor: string | null, signal: AbortSignal): Promise<Page<RecordingRecord>> =>
      userId === null
        ? Promise.resolve({ items: [], next_cursor: null })
        : listRecordings(userId, { cursor }, { signal }),
    [userId],
  )

  /**
   * ONE page, not Today's budget of three.
   *
   * §4.5 says to **expose** further pages, not to consume them: this screen is
   * where a user goes to look through their history, so reading ahead on their
   * behalf spends requests on rows they may never scroll to. It also keeps
   * `budgetSpent` unreachable, which is one fewer state the range statement has
   * to carry and one fewer sentence to get wrong.
   */
  const list = usePagedList<RecordingRecord>(fetchPage)
  const rows = list.items

  /**
   * All three of these are effect dependencies of `useFanOut`, and its own first
   * `setEntries` causes a render — so an unmemoised one re-runs the effect on
   * every render and aborts every request before it can finish. Memoised on
   * `rows`, they change identity exactly once per page, which is also when the
   * key list changes.
   */
  const keys = useMemo(() => rows.map((r) => r.recording_id), [rows])
  const statusById = useMemo(
    () => new Map(rows.map((r) => [r.recording_id, r.status] as const)),
    [rows],
  )

  // §4.1: "No Daily Status request for `empty`/`failed` rows." Read through
  // `hasDailyStatus`, which reads `RECORDING_OUTCOME[...].hasResult` — one copy
  // of that four-value fact, for the reason recordings.ts:50-57 records.
  const eligible = useCallback(
    (id: string) => hasDailyStatus(statusById.get(id) ?? ''),
    [statusById],
  )

  /**
   * Narrowed HERE rather than in the cells, so `readDailyStatus` owns the
   * preview / shippable / schema contract (§1.7) in one reviewable place — and
   * so the trend chart consumes the same read model the table does rather than
   * re-deriving it from raw JSON.
   */
  const fetchOne = useCallback(
    (id: string, signal: AbortSignal): Promise<DailyStatusResult> =>
      getDailyStatus(id, { signal }).then(readDailyStatus),
    [],
  )

  const fan = useFanOut<string, DailyStatusResult>(keys, fetchOne, { eligible })
  const range = historyRangeText(historyRange(list))
  /**
   * ONE expression gates the "Load more" CONTROL and the sentence that names
   * it. They were gated on different predicates, which is trap 31's shape: on a
   * total failure `EmptyHistory` rendered "Load more to look further back."
   * while this evaluated false, so the screen told a person to press something
   * that was not on it.
   */
  const canLoadMore = !list.isLoading && (rows.length > 0 || list.nextCursor !== null)
  // The notice below unmounts when the failure clears; see `useRetryLanding`.
  const [landOn, armLanding] = useRetryLanding(range.failure !== null)

  /**
   * CRITERION 9, WITHOUT THE CLIENT WRITING THE DISCLAIMER.
   *
   * This screen shows confidence values, so it owes a disclaimer. But
   * `disclaimer` is a payload field rendered verbatim and never paraphrased
   * (§1.5); a copy hard-coded here would be a client-authored governed string,
   * which is exactly what criterion 2 forbids. So it comes from the payloads
   * that actually loaded, deduplicated — and when none has, there is none to
   * show and also nothing on screen that needs disclaiming.
   */
  const disclaimers = useMemo(() => {
    const seen = new Set<string>()
    for (const key of keys) {
      const entry = fan.entries[key]
      if (entry?.status !== 'ready') continue
      const value = entry.value
      if (value === undefined || !value.readable || value.disclaimer === null) continue
      seen.add(value.disclaimer)
    }
    return [...seen]
  }, [keys, fan.entries])

  /**
   * READINGS OR JOURNAL — one page, one query, one pagination (concept §B).
   *
   * Only the columns fork. The segmented control below switches them, the
   * choice is remembered per browser by `lib/historyView.ts`, and nothing else
   * on the screen is conditional on it: the same rows, the same range line,
   * the same single `LoadMore`.
   *
   * Read from storage during the FIRST render rather than in an effect, the
   * same call `ThemeProvider` makes: a view that mounts as Readings and
   * corrects itself to Journal a frame later is a flash of the wrong screen on
   * the one page where a user asked for the other.
   */
  const [storedView, setView] = useState<HistoryView>(readStoredHistoryView)
  const view = experimentView ?? (storedView === 'day' ? 'readings' : storedView)
  const chooseView = useCallback((next: HistoryView) => {
    setView(next)
    storeHistoryView(next)
    onExperimentView?.(next)
  }, [onExperimentView])
  const isJournal = view === 'journal'

  /**
   * HOW THE DAYS ARE GATHERED — the Journal view's second control (concept §B2).
   *
   * `'day'` is the ungrouped list the Journal view opens in, and it is a named
   * state rather than "neither segment pressed": `SegmentedControl` has no
   * deselect, so with two segments a reader who pressed By week could never get
   * back to the day list. `lib/historyView.ts` says the same thing once more
   * where the words live.
   *
   * Opening a collapsed period is per-period state and is dropped whenever the
   * grouping changes, because the keys of one grouping mean nothing in the
   * other: `week:2026-09-28` is not a month.
   */
  const [grouping, setGrouping] = useState<JournalGroupingChoice>(readStoredJournalGrouping)
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const chooseGrouping = useCallback((next: JournalGroupingChoice) => {
    setGrouping(next)
    storeJournalGrouping(next)
    setExpanded(new Set())
  }, [])
  const expand = useCallback((key: string, open: boolean) => {
    setExpanded((before) => {
      const after = new Set(before)
      if (open) after.add(key)
      else after.delete(key)
      return after
    })
  }, [])

  /**
   * THE ONE READ THE JOURNAL VIEW ADDS, and only while it is on screen.
   *
   * `journal-summary` carries every number the grouped views show, plus the
   * coverage counts the day list shows — §4.1's rule is that this screen asks
   * for nothing it cannot display, so in the Readings view the read resolves to
   * `null` without a request. `group` is the only input that changes the answer;
   * `'day'` asks for weeks, because the day list reads `usual` and ignores
   * `groups`, and switching day↔week then needs no second request.
   *
   * The zone is the browser's, as `/home`'s caller sends it: it decides the
   * times of day in the payload and nothing about which day a recording belongs
   * to, which is the recording's own `session_date`.
   */
  const summaryGroup: JournalGrouping = grouping === 'month' ? 'month' : 'week'
  const loadSummary = useCallback(
    (signal: AbortSignal): Promise<JournalSummaryResponse | null> =>
      userId === null || !isJournal
        ? Promise.resolve(null)
        : getJournalSummary(
            userId,
            { group: summaryGroup, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
            { signal },
          ),
    [userId, isJournal, summaryGroup],
  )
  const summary = useResource<JournalSummaryResponse | null>(loadSummary)

  /**
   * The payload's periods, each holding the loaded rows that fall in it.
   *
   * Built whenever the summary is there, in every Journal state, so the day
   * list and the grouped views are never looking at two different readings of
   * the same payload. Only `groups` below decides whether the headers render.
   */
  const periods = useMemo(
    () => (summary.data === null ? [] : journalPeriods(rows, summary.data, { expanded })),
    [summary.data, rows, expanded],
  )
  const usual = summary.data?.usual ?? null

  /**
   * One period's footer, and it is NEVER a second "Load more".
   *
   * Two different lines, and only one of them is a control:
   *
   *   - a collapsed period the page has read at least one day of gets a
   *     disclosure, "Show the 6 days" (concept §B3). The period's title is
   *     directly above it, so the visible words do not repeat it; the
   *     accessible name does, because a screen reader reaching the button by
   *     its role has not read the title. It names the period
   *     (`formatPeriodName`) and not the payload's label, so it reads "Show the
   *     2 days of 24 Aug" rather than "… of Week of 24 Aug".
   *   - a period the page has only PART of gets a muted sentence and nothing to
   *     press. A second control named "Load more" beside the page's own is a
   *     cost `History.tsx` has recorded twice already — and it would be a
   *     control that cannot keep its promise, because the list pages by
   *     `created_at` and a period is a range of `session_date`s.
   *
   * **Both lines read what is ON SCREEN, and the muted one is not conditional
   * on the period being open.** A collapsed period whose rows the list has not
   * reached is the normal state, not an edge: the summary pages independently
   * of the recordings list. Such a period showed a disclosure that named three
   * days and revealed none, and admitted the shortfall only after it had been
   * pressed. So the disclosure now needs a day to disclose, and the shortfall
   * is stated whether the period is open or shut.
   *
   * The day count in both is the payload's `coverage.days`; `loadedDays` is the
   * one number this page produces, and it is a fact about this page.
   */
  const periodFooter = (period: JournalPeriod) => {
    const partial = period.loadedDays !== period.days
    const disclosable = period.collapsible && period.loadedDays > 0
    if (!disclosable && !partial) return null
    return (
      <>
        {disclosable && (
          <button
            type="button"
            className="journal-disclosure"
            aria-expanded={!period.collapsed}
            onClick={() => expand(period.key, period.collapsed)}
          >
            {period.collapsed ? 'Show' : 'Hide'} the {period.days}{' '}
            {period.days === 1 ? 'day' : 'days'}
            <span className="visually-hidden"> of {period.periodName}</span>
          </button>
        )}
        {partial && (
          <span className="journal-partial">
            Showing {period.loadedDays} of {period.days} {period.days === 1 ? 'day' : 'days'}
          </span>
        )}
      </>
    )
  }

  /**
   * `undefined` — today's path, byte for byte — unless the Journal view is
   * grouped AND the payload arrived. `DataTable` documents that `undefined` is
   * the markup the admin console renders through, and the day list is the same
   * flat table it has always been.
   */
  const groups: RowGroup<RecordingRecord>[] | undefined =
    isJournal && grouping !== 'day' && periods.length > 0
      ? periods.map((period) => ({
          key: period.key,
          label: period.label,
          header: (
            <GroupHeader
              title={period.label}
              coverage={period.coverage}
              facts={period.facts}
              unavailableText={period.unavailableText}
            />
          ),
          rows: period.rows,
          collapsed: period.collapsed,
          footer: periodFooter(period),
        }))
      : undefined

  const readingColumns: Column<RecordingRecord>[] = [
    {
      key: 'date',
      header: 'Date',
      label: 'Recording date',
      // The upload time travels with the row because the list is SORTED by it
      // while this column shows the recording date. Without it, page one reads
      // 11 September then 30 August and looks like a broken sort.
      render: (r) => {
        const pair = pairOf(r)
        return (
          <span className="history__date">
            <span
              className="mono-cell mono-cell--strong"
              title={`Uploaded ${formatDateTime(r.created_at)}`}
            >
              {formatSessionDate(r.session_date)}
            </span>
            {/* The paired session (Sprint 1.9.13 W4, concept E): two rows,
                because they are two recordings, each saying which block it is. */}
            {pair !== null && <span className="history__detail history__block">block {pair.pairIndex} of 2</span>}
          </span>
        )
      },
    },
    {
      key: 'condition',
      header: 'Condition',
      render: (r) => {
        const pair = pairOf(r)
        return (
          <span className={pair === null ? undefined : 'history__pair'}>
            {pair !== null && <Icon name="link" className="history__pair-icon" />}
            {CONDITION_WORDS[r.condition]} <span className="mono-cell">{r.condition}</span>
            {pair !== null && <span className="history__detail"> · paired</span>}
          </span>
        )
      },
    },
    {
      key: 'state',
      header: 'State',
      render: (r) => <StateCell row={r} entry={fan.entries[r.recording_id]} />,
    },
    {
      key: 'confidence',
      header: 'Confidence',
      align: 'end',
      render: (r) => (
        <ConfidenceCell
          entry={fan.entries[r.recording_id]}
          onRetry={() => fan.retry(r.recording_id)}
          forDate={formatSessionDate(r.session_date)}
        />
      ),
    },
    {
      key: 'journal',
      header: 'Self-report',
      render: (r) => <SelfReportCell record={r} pair={pairOf(r)} />,
    },
  ]

  const columns = isJournal ? JOURNAL_COLUMNS : readingColumns

  return (
    <div className="stack stack--wide history">
      {/* The eyebrow repeats the `<h1>` verbatim, so it is decoration and is
          hidden from assistive tech (R25). The retry landing target on this
          screen is the range line below, not the header, so no ref is passed. */}
      <PageHeader
        eyebrow="History"
        eyebrowHidden
        title="History"
        lead="Every session you have uploaded, newest first. Open one to see its result and the journal for that day."
      />

      {/* A read that failed PART WAY does not discard what it read (Today.tsx:119-132).
          The rows that arrived are real; the range line below is what stops them
          from reading as the whole list. */}
      {/* The list-level failure now carries the server's words and its code, and
          a retry of its own. It had none: the only retry on this screen was
          inside the range line below, and `historyRange` produces one for two of
          its kinds only -- so a failure landing on any other kind rendered a
          sentence with nothing to press. */}
      {/* ── ONE FAILURE, ONE MESSAGE, ONE RETRY ──────────────────────────────
          This screen used to say the same thing twice and disagree with itself
          doing it: the notice said "so this is not all of it", implying a
          partial list, directly above a range line saying "None of your history
          could be read." Both were about completeness and on a total failure
          only one of them could be true.

          The notice owns the FAILURE, because §7.1 wants the server's message
          and its code and `historyRangeText` returns a bare string that cannot
          carry either. The range line owns the EXTENT (§4.5, criterion 16). One
          predicate decides both now, so they cannot contradict each other.

          AND THE RETRY IS THE RIGHT ONE. `historyRange` works out which action
          a failure deserves, under a comment saying in terms that the wrong one
          "is destructive rather than merely useless" -- and this call site
          passed `list.reload` unconditionally, so on a stalled read the button
          threw away every row that had arrived. It uses the answer the module
          next door had already computed. */}
      {range.failure !== null && (
        <ProblemNotice
          error={list.error}
          onRetry={range.retry === 'loadMore' ? list.loadMore : list.reload}
          landing={armLanding}
        >
          {range.failure}
        </ProblemNotice>
      )}

      {/* No button here any more. There were two named "Try again" forty pixels
          apart doing different things -- and a third on every failing row. That
          collision has already produced one false-green test
          (`problemCodes.test.tsx:142-144`). */}
      <p className="history__range" role="status" ref={landOn} tabIndex={-1}>
        {range.text}
      </p>

      {/* Handed what the list already read, and given no way to fetch anything
          itself — which is how §4.5's "keep chart requests bounded as pages
          load" is satisfied. */}
      {/* ── THE CHART IS THE READINGS VIEW'S (concept §B, §B2/§B3) ──────
          It plots READINGS — a score slot per session against its baseline —
          and the Journal view below plots nothing: it reads back the answers a
          person wrote. The concept shows no chart above the journal table in
          any of its three states, and that was the last visible difference
          from those screenshots (flagged in J2b and again in J2c). The spec's
          own wording is that this sprint "adds the Journal view beside it and
          changes nothing else on that canvas", so the READINGS view keeps the
          chart untouched. One line, so the owner reverses it by deleting
          `!isJournal && `. */}
      {!isJournal && <TrendChart rows={rows} entries={fan.entries} isComplete={list.isComplete} />}

      {/* ── THE TWO VIEWS ──────────────────────────────────────────────────
          `SegmentedControl` as it stands: `role="group"` with `aria-pressed`
          per option, which is the call that component documents at length and
          `controls.test.ts` holds it to. The sprint spec's acceptance line
          "it is a `radiogroup`" is older than that decision and is recorded as
          a deviation in the LOG rather than built.

          The label is hidden because the two words name themselves, and the
          group still has an accessible name -- `hideLabel` renders it
          `visually-hidden` rather than dropping it. */}
      <div className="history__views">
        <SegmentedControl
          label="History view"
          hideLabel
          options={experimentView === undefined ? HISTORY_VIEW_OPTIONS.filter((option) => option.value !== 'day') : HISTORY_VIEW_OPTIONS}
          value={view}
          onChange={chooseView}
        />
        {/* ── AND HOW THE DAYS ARE GATHERED (concept §B2) ─────────────────
            Three segments, in the Journal view only: the day list is a state
            of its own with its own footer, and this control cannot be
            deselected back into it. Same component, same `role="group"` and
            `aria-pressed`, its own accessible name. */}
        {isJournal && (
          <SegmentedControl
            label="Group the days"
            hideLabel
            options={JOURNAL_GROUPING_OPTIONS}
            value={grouping}
            onChange={chooseGrouping}
          />
        )}
      </div>

      {/* The summary is what carries every number below, so a failure to read
          it says so and offers the read again -- rather than letting By week
          look like By day for reasons nothing on screen explains. */}
      {isJournal && summary.error !== null && (
        <ProblemNotice error={summary.error} onRetry={summary.reload}>
          Your journal summary could not be read, so these days are not grouped or summarised.
        </ProblemNotice>
      )}

      {/* ── THE THREE PLAIN COUNTS (concept §B) ────────────────────────────
          Answered, skipped and edited, over the WHOLE history rather than over
          the rows this page has loaded -- `usual.coverage` is the payload's own
          count and the web subtracts nothing from it (`skipped` is sent, not
          derived).

          IN ALL THREE STATES, which is not where the concept draws it (owner
          decision, 2026-10-08). The concept puts the counts under §B alone, and
          that separation is an artifact of each panel having been drawn for one
          state: these three counts describe the WHOLE history -- the same numbers
          whichever way the days below are gathered -- so dropping them when a
          reader presses By week withholds a fact about their history because of a
          choice about its layout. The per-period coverage lines are a different
          claim (this week, this month) and stay where they are. */}
      {isJournal && usual !== null && (
        <p className="journal-coverage">
          <span>
            Journal answered for <b>{usual.coverage.journals} of {usual.coverage.recordings}</b>{' '}
            {usual.coverage.recordings === 1 ? 'recording' : 'recordings'}
          </span>
          <span><b>{usual.coverage.skipped}</b> skipped</span>
          <span><b>{usual.coverage.edited}</b> edited later</span>
        </p>
      )}

      {/* ── "YOUR USUAL" (concept §B2) ─────────────────────────────────────
          Every answered journal together, pinned above the table so the eye can
          compare a week with it. The page says nothing about the comparison and
          neither does the payload: no arrow, no delta, no colour by value.

          IN ALL THREE STATES TOO (owner decision, 2026-10-08): a person does not
          stop having a usual when they group their days, and it is the anchor
          every group number is read against. The concept draws it under §B2/§B3
          only for the same reason as above -- each panel was drawn for one state.
          `pinned` is the one "Your usual" component and this is its one call
          site; the day list gets that component, not a second one.

          It sits directly above the table in every state, under the counts line,
          so the order on screen no longer changes with the grouping either. */}
      {isJournal && usual !== null && (
        <GroupHeader
          pinned
          title="Your usual"
          coverage={usualCoverageText(usual)}
          facts={summaryFacts(usual.facts)}
          // `usual` carries its own `unavailable_reason`, so the sentence under
          // an empty line is the payload's and not the header's default.
          unavailableText={unavailableText(usual)}
        />
      )}

      <DataTable
        // The table's accessible name follows the view: the rows are the same
        // recordings, but what the columns say about them is not.
        caption={isJournal ? 'Your journals' : 'Your recordings'}
        columns={columns}
        rows={rows}
        rowKey={(r) => r.recording_id}
        // `list.isLoading` and NEVER `list.isPaging`: this prop replaces the whole
        // table with a spinner (DataTable.tsx:65-72), and a second page arriving
        // must not blank the rows the user is reading.
        isLoading={list.isLoading}
        // The same rows, partitioned -- and `undefined` everywhere else, which
        // `DataTable` pins as today's markup byte for byte.
        groups={groups}
        // NULL IS COMPUTED HERE, not inside `EmptyHistory`. `DataTable`
        // branches on the PROP (DataTable.tsx:75-77), and an
        // `<EmptyHistory />` element is a truthy object whatever the component
        // returns -- so a component returning null would still have rendered
        // the bordered empty panel, and a guard inside it would have been a fix
        // that could not fire.
        empty={
          range.failure !== null
            ? null
            : <EmptyHistory isComplete={list.isComplete} />
        }
        actions={(r) => (
          <Link
            className="btn btn--ghost btn--sm"
            data-recording-id={r.recording_id}
            to={`/history/${encodeURIComponent(r.recording_id)}`}
          >
            {/* Six rows means six links, and "Open" six times is what a screen
                reader reads out when someone navigates a page by its links. The
                visible word stays the visible word — extending the accessible
                name rather than replacing it keeps the label a person says out
                loud a prefix of the one the machine reports. */}
            Open<span className="visually-hidden"> {formatSessionDate(r.session_date)}</span>
          </Link>
        )}
      />

      {/*
        Two guards, not one, and the second is not `rows.length > 0`.
        `LoadMore` prints "End of the list." whenever `nextCursor` is falsy —
        INCLUDING the initial null, before any page has landed — so the
        `isLoading` guard is what stops it declaring the end of a history it has
        not seen. But gating on rows alone leaves a state that TELLS a person to
        load more and renders nothing to press: a page that came back empty with
        a cursor still set. Gating on the cursor as well means the control exists
        exactly when there is another page, which is what the sentence promises.
      */}
      {canLoadMore && (
        <LoadMore
          loaded={rows.length}
          nextCursor={list.nextCursor}
          isLoading={list.isPaging}
          noun="recording"
          onLoadMore={list.loadMore}
        />
      )}

      {/* ── THE FOOTER (concept §B, §B2/§B3) ───────────────────────────────
          Presentation copy, verbatim from the concept, and it says what this
          view is and is not. Two sentences for two different claims: the day
          list shows answers as they were written, the grouped views show
          averages and counts OF those answers — and both end by saying nothing
          here is compared with a reading, which is the one thing a page of
          numbers beside a page of EEG results could be read as doing.

          The link is in all three states, although the concept draws it only
          under §B: it is the anchor to the research notes below, and a reader
          who grouped their days has not stopped being owed it. */}
      {isJournal && (
        <p className="journal-foot">
          {grouping === 'day' ? JOURNAL_FOOT_DAYS : JOURNAL_FOOT_GROUPED}{' '}
          <a className="journal-foot__link" href={`#${RESEARCH_NOTES_ID}`}>
            How the journal is used
          </a>
        </p>
      )}

      {disclaimers.map((text) => (
        <aside key={text} className="disclaimer" aria-label="Disclaimer">
          {text}
        </aside>
      ))}

      {/* ── R73: THE RESEARCH NOTES, AT THE END OF THE PAGE ────────────────
          The anchor above needs a target, and `/history` has never mounted one.
          Through `<PageEnd>`, so inside the shell it lands after everything
          this screen renders.

          THE LIST PAGE ONLY. `/history/:id` renders `DailyStatusView`, which
          mounts its own `ResearchNotes` — and `RESEARCH_NOTES_ID` is a single
          document id, so a second one on the same page would make the anchor
          ambiguous and `aria-labelledby` resolve to whichever came first. The
          two screens are separate routes, so one each.

          Only in the Journal view, because only the Journal view links to it.
          Nothing scientific is written here: the note says what the journal is
          not used for, which is a fact about the product. */}
      {isJournal && (
        <PageEnd>
          <ResearchNotes testid="history-research-notes" label="How the journal is used">
            <p className="research-notes__item">
              Your journal is stored exactly as you wrote it and is not read by any scoring
              path: no reading on this site is derived from it, and no number here is compared
              with one. Using it as evidence needs a validated method and a version to cite,
              and when there is one the page will say what it compared and on which days.
            </p>
          </ResearchNotes>
        </PageEnd>
      )}
    </div>
  )
}


/**
 * The recording's terminal state — NOT the journal's felt state.
 *
 * §4.1 lists "state" and "whether a self-report exists" as separate fields, and
 * §3.2 calls the journal's answer a "felt state … never an EEG-derived emotion
 * label". §2.5 uses this word for exactly this vocabulary: "`RecordingRecord` is
 * terminal history (`scored`, `rejected`, `empty`, `failed`)". And criterion 8
 * has nowhere else to live — History is the only screen that lists all four.
 *
 * Putting a self-reported mood in a column beside a confidence number is the
 * fastest way to make it look like a measurement, which the Non-goals forbid.
 * It appears on the detail view instead, inside the form that authored it.
 */
function StateCell({
  row,
  entry,
}: {
  row: RecordingRecord
  entry: FanOutEntry<DailyStatusResult> | undefined
}) {
  const outcome = RECORDING_OUTCOME[row.status]
  return (
    <span className="history__state">
      <RecordingStatusPill status={row.status} />
      {/* `empty` gets its own sentence and nothing that reads as a fault. Its
          pill tone is already neutral and `isError: false` keeps it out of every
          error style — §2.6, and criterion 8 by name. */}
      {row.status === 'empty' && outcome != null && (
        <span className="history__detail">{outcome.detail}</span>
      )}
      {row.status === 'rejected' && <RejectionReason entry={entry} />}
      {row.status === 'failed' && <FailureReason code={row.error_code ?? null} />}
    </span>
  )
}

/**
 * WHY a recording failed, from the row itself (ask B7).
 *
 * It has to come from the row, not from a payload: `History` deliberately asks
 * for no Daily Status for a failed recording — there is none — so the fan-out
 * that feeds `RejectionReason` is never run here. Until `error_code` shipped,
 * the reason lived only on the Job, and `RecordingRecord` carries no job id
 * while a failed job carries no `result_recording_id`; the link existed in one
 * direction and it was the wrong one.
 *
 * A code this build does not recognise renders as the raw token, in the same
 * `.mono-cell` `RejectionReason` uses. An unmapped code that said nothing would
 * put us back where we started.
 */
function FailureReason({ code }: { code: string | null }) {
  const outcome = RECORDING_OUTCOME.failed
  if (code === null) {
    // Scored before the field existed. The old sentence is still the true one.
    return <span className="history__detail">{outcome?.detail}</span>
  }
  const copy = ERROR_CODE_COPY[code]
  return (
    <span className="history__detail">
      {copy ?? <code className="mono-cell">{code}</code>}
    </span>
  )
}

/**
 * Criterion 8's "rejected recordings are visible **with their reason**".
 *
 * The reason is the payload's, reached because `rejected` carries
 * `hasResult: true` and is therefore fanned out. Nothing here authors one: until
 * the payload arrives, the standing sentence is `RECORDING_OUTCOME`'s, which
 * says a rejected recording is kept rather than deleted and does not guess why.
 */
function RejectionReason({ entry }: { entry: FanOutEntry<DailyStatusResult> | undefined }) {
  const value = entry?.status === 'ready' ? entry.value : undefined
  if (value === undefined || !value.readable) {
    return <span className="history__detail">{RECORDING_OUTCOME.rejected?.detail}</span>
  }
  const { shown } = presentWarnings(value.activeWarnings)
  const first = shown[0]
  const reason = value.validity?.reasons?.[0] ?? null
  if (first !== undefined) {
    return (
      <span className="history__detail">
        {first.copy ?? <code className="mono-cell">{first.code}</code>}
      </span>
    )
  }
  if (reason !== null) {
    return (
      <span className="history__detail">
        <code className="mono-cell">{reason}</code>
      </span>
    )
  }
  return <span className="history__detail">{RECORDING_OUTCOME.rejected?.detail}</span>
}

/**
 * The five fan-out states, as a `switch` and not a run of `&&`s.
 *
 * Today's first version was the latter and one state rendered **nothing at all**
 * (`Today.tsx:162-173`). In a table that is worse than on a page: an empty cell
 * beside four full ones reads as a measured absence rather than as a screen that
 * has not finished.
 */
function ConfidenceCell({
  entry,
  onRetry,
  forDate,
}: {
  entry: FanOutEntry<DailyStatusResult> | undefined
  onRetry: () => void
  /** Only to distinguish this row's retry from every other row's. */
  forDate: string
}) {
  // `undefined` is REAL: on the render where rows first appear, the fan-out
  // effect has not run yet. Folded into `pending` so the switch below covers the
  // five declared values and no path renders blank.
  const state = entry ?? { status: 'pending' as const }

  switch (state.status) {
    case 'skipped':
      // Not a dash, and not an error. No request was made, because §4.1 says one
      // must not be: an `empty` recording has no result by construction and a
      // `failed` one never got that far.
      return <span className="history__none">No result</span>

    case 'pending':
    case 'loading':
      return (
        <span className="loading-row" role="status">
          <span className="spinner" aria-hidden="true" /> Loading
        </span>
      )

    case 'error':
      return <ConfidenceError error={state.error} onRetry={onRetry} forDate={forDate} />

    case 'ready': {
      const result = state.value
      if (result === undefined) return <span className="history__none">No result</span>
      // §1.7 — an unusable payload gets a clear unavailable state, never scores.
      if (!result.readable) {
        return (
          <span className="history__none" title={result.detail}>
            No usable result
          </span>
        )
      }
      // Criterion 3, applied to confidence: `None` is unavailable, never zero.
      const confidence = formatConfidence(result.confidence)
      if (confidence === null) {
        return <span className="history__none">Not available for this session</span>
      }
      return (
        <span className="history__confidence">
          {/* The one confidence formatter, exactly as `ConfidenceBanner` writes
              it (UI-QA Q8), and not a percentage: the client has no scale to
              convert it to, and the same number must not read differently on
              two screens. */}
          <span className="mono-cell">{confidence}</span>
          {/* Criterion 4 in a list. "Held low" is already Baseline's phrase for
              this fact; a second wording would be a second vocabulary. */}
          {result.lowConfidence && (
            <Pill tone="amber" dot>
              Held low
            </Pill>
          )}
        </span>
      )
    }
  }
}

function ConfidenceError(
  { error, onRetry, forDate }: { error: unknown; onRetry: () => void; forDate: string },
) {
  const problem = error instanceof ApiError ? error.problem : null
  return (
    <span className="history__error">
      {/* A 404 here is a `scored` or `rejected` row whose stored result is not
          there. That is a storage fact, not a quality verdict, and it must not
          borrow the word `failed` owns — criterion 8 in miniature. */}
      <span>
        {problem?.status === 404 ? 'No stored result for this recording.' : 'Could not load.'}
      </span>
      {/* §7.1: a backend Problem renders its message AND its code. */}
      {problem !== null && <code className="mono-cell">{problem.code}</code>}
      {/* NAMED, because there is one of these per failing row and one more in
          the notice above, and they do different things. A screen reader
          reading a list of identical "Try again" buttons cannot tell which row
          it is on -- and the collision has already produced one false-green
          test in this repo (`problemCodes.test.tsx:142-144`), where an
          assertion about the notice's retry was satisfied by a different
          control entirely. The visible label stays short. */}
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={onRetry}
        aria-label={`Try again for ${forDate}`}
      >
        Try again
      </button>
    </span>
  )
}

/**
 * Whether a self-report exists, and what the server recorded about where it came
 * from — §4.1, including the clause that matters: **do not claim an unknown
 * historical report was captured or manually entered.**
 *
 * `provenanceWords` already answers "Source not recorded" for an unknown origin
 * and "Edits not recorded" for a null `edited`. The only rule this component
 * adds is not to substitute anything when it returns `null`: a report with no
 * provenance row at all gets no origin sentence, because there is no claim
 * available to make.
 */
function SelfReportCell({ record, pair }: { record: RecordingRecord; pair: PairLink | null }) {
  // The journal belongs to the pair and sits on the eyes-closed row; the
  // eyes-open row says so rather than inviting one (1.9.13 W4, concept E).
  if (record.self_report == null && pair !== null && pair.pairRole === 'EO') {
    return <span className="history__none">With block 2</span>
  }
  if (record.self_report == null) return <span className="history__none">None yet</span>

  const words = provenanceWords(record.self_report_provenance)
  return (
    <span className="history__journal">
      {/* Not "Self-report": that is the column's own label, so the card view at
          phone width read "Self-report: Self-report". The value has to say
          whether there is one, not repeat the question. */}
      <Pill tone="neutral" dot>
        Filed
      </Pill>
      {words !== null && (
        <span className="history__provenance">
          {words.origin}
          {words.edited !== null ? ` · ${words.edited}` : ''}
        </span>
      )}
    </span>
  )
}

/**
 * `DataTable` renders `empty` whenever there are no rows and has no notion of
 * completeness, so the distinction §4.5 asks for has to be made here: "you have
 * never recorded" and "nothing on the pages read so far" are different facts and
 * only one of them routes to Record.
 */
function EmptyHistory({ isComplete }: { isComplete: boolean }) {
  /**
   * ── THE DEAD END IS FIXED AT THE CALL SITE, NOT HERE ─────────────────────
   *
   * This sentence NAMES a control, and it used to render while `LoadMore` did
   * not: on a first-page failure `pagesLoaded` is 0, so `isComplete` was false
   * and this line appeared, while the control needs a row or a cursor and
   * appeared nowhere. Trap 31's shape -- a sentence and the control it names on
   * different predicates.
   *
   * The first fix for it was a `canLoadMore` prop here, and it was removed
   * again after measuring: with `empty` computed null at the call site whenever
   * the read failed, this component is not rendered in the only state where the
   * two predicates diverge, so the guard could not fire and no mutation of it
   * reddened anything. Within the empty slot the divergence needs
   * `pagesLoaded === 0`, no cursor and no loading -- which is the failed read
   * and nothing else.
   *
   * The range line above already says "no recordings on the pages read so far",
   * so this one says the thing that line does not: what to do about it. Two
   * elements repeating one sentence is how a screen reader hears it twice.
   */
  if (!isComplete) return <p>Load more to look further back.</p>
  return (
    <Card>
      <div className="product-empty">
        <h2 className="product-empty__title">No recordings yet</h2>
        <p className="product-empty__body">
          Your history fills up as you record. Nothing is measured until you record
          something.
        </p>
        <Link className="btn btn--primary" to="/record">
          Record a session
        </Link>
      </div>
    </Card>
  )
}
